import { useEffect } from 'react';

import { SecondaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import { ResponsiveDesignProvider } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignProvider';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import cx from 'classnames';
import { Provider } from 'react-redux';

import ClientApp from '~/ClientApp';
import ClientRouter from '~/ClientRouter';
import MainLayout from '~/components/common/layouts/MainLayout/MainLayout';
import { UserContextProvider } from '~/contexts/UserContext';
import { ExpertAdviceForSRP } from '~/pages/couples/explore/components/ExpertAdvice/ExpertAdviceForSrp';
import { PageSection } from '~/pages/couples/explore/components/PageSection/PageSection';
import { AdjacentVendors } from '~/pages/couples/explore/components/SearchResults/components/AdjacentVendors/AdjacentVendors';
import { RealWeddings } from '~/pages/couples/explore/components/SearchResults/components/RealWeddings/RealWeddings';
import { SearchResultsPageBreadcrumbs } from '~/pages/couples/explore/components/SearchResults/components/SearchBreadcrumb/SearchBreadcrumb';
import { SearchResultsHead } from '~/pages/couples/explore/components/SearchResults/components/SearchResultsHead/SearchResultsHead';
import { SeoContentBlock } from '~/pages/couples/explore/components/SearchResults/components/SeoContentBlock/SeoContentBlock';
import {
  SearchResultsPageProvider,
  SearchResultsPageProviderProps,
} from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageProvider';
import { VendorSearchNavigationProvider } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationProvider';
import { SearchResultsContent } from '~/pages/couples/explore/components/SearchResults/SearchResultsContent';
import SearchResultsPage from '~/pages/couples/explore/SearchResultsPage';
import { ParseVendorSearchUrlResult } from '~/pages/couples/explore/util/parseVendorSearchUrl';
import { getStore } from '~/store';
import { IUserContext } from '~/types/userContext';
import { isAutomatedTest } from '~/util/automatedTestUtils';
import EXPERIMENT_FLAGS from '~/util/experimentFlags';

import styles from './controlPage.module.less';

export type ControlPageProps = SearchResultsPageProviderProps & {
  navHtml: string | null;
  parsedUrl: ParseVendorSearchUrlResult;
  userAgent: string | undefined;
  userContext: IUserContext | null;
  isPricingRatingTest1: boolean;
  isPricingRatingTest2: boolean;
};

export const ControlPage = ({
  identifiedAsVendor,
  navHtml,
  userAgent,
  userContext,
  isPricingRatingTest1,
  isPricingRatingTest2,
  ...rest
}: ControlPageProps) => {
  useEffect(() => {
    // Trigger experiment viewed
    if (!identifiedAsVendor) {
      EXPERIMENT_FLAGS.isUnderTest('marketplace_featured_vendors_holdout', userContext, true);
    }
  }, [identifiedAsVendor, userContext]);

  useEffectOnce(() => {
    // Trigger experiment viewed
    !isAutomatedTest() &&
      EXPERIMENT_FLAGS.isUnderTest('marketplace-search-results-header-test', userContext, true);
  });

  return (
    <UserContextProvider userContext={userContext}>
      <ResponsiveDesignProvider userAgent={userAgent} useV2>
        <Provider store={getStore()}>
          <MainLayout
            activeSecondaryLink={SecondaryNavIdentifier.EXPLORE_VENDORS}
            ssrNavHtml={navHtml}
            className={cx(styles.mainLayout)}
            showSecondaryNavOnLoad={userContext ? userContext?.is_guest : true}
          >
            <SearchResultsPageProvider {...rest} identifiedAsVendor={identifiedAsVendor}>
              <VendorSearchNavigationProvider>
                <SearchResultsHead />

                <SearchResultsContent
                  fromSrp
                  isPricingRatingTest1={isPricingRatingTest1}
                  isPricingRatingTest2={isPricingRatingTest2}
                />

                <ClientApp useStore={false}>
                  <ClientRouter>
                    <SearchResultsPage />
                  </ClientRouter>
                </ClientApp>

                <AdjacentVendors
                  fromSrp
                  isPricingRatingTest1={isPricingRatingTest1}
                  isPricingRatingTest2={isPricingRatingTest2}
                />
                <ExpertAdviceForSRP />

                <RealWeddings />
                <PageSection>
                  <SeoContentBlock />
                </PageSection>
                <SearchResultsPageBreadcrumbs location="bottom" />
              </VendorSearchNavigationProvider>
            </SearchResultsPageProvider>
          </MainLayout>
        </Provider>
      </ResponsiveDesignProvider>
    </UserContextProvider>
  );
};
